/**
 * /app/{tenantSlug}/me/loans — Me → Pay → Loans: my own loans and salary
 * advances, with what I still owe and a Request button.
 *
 * Navigation rebuild (wave 3, 28 Oct 2026): replaces the employee view of
 * /loans (now a redirect stub). Only the viewer's own loans show here, for
 * every role: the list asks for ?employee_id=<me> (the server forces a
 * non-finance caller to self anyway, and honours the filter for finance),
 * and the summary without employee_id is always the caller's own. HR's
 * company-wide list is Manage → Payroll → Loans & Advances (/loans/manage);
 * requests waiting on a finance decision are in the Inbox.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toApiError } from "@/lib/api";
import { useLoans, useLoanSummary } from "@/hooks/use-loans";
import { formatPaise } from "@/lib/expense-format";
import { LoanTable } from "@/components/loans/loan-table";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/loans")({
  component: MyLoansRoute,
});

function MyLoansRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const selfId = user?.user_id;

  const loansQ = useLoans({ employeeId: selfId });
  const { data: summary } = useLoanSummary();
  // Belt and braces: the list is already asked for the viewer only.
  const loans = loansQ.data?.filter((l) => l.employee_id === selfId);

  const requestButton = (
    <Button asChild className="gap-1.5" data-testid="loans-new-button">
      <Link to="/app/$tenantSlug/loans/new" params={{ tenantSlug }}>
        <Plus aria-hidden className="h-4 w-4" />
        Request
      </Link>
    </Button>
  );

  return (
    <MeModuleShell
      title="Loans"
      description="Your interest-free staff loans and salary advances, recovered from your monthly pay."
      actions={requestButton}
    >
      <div className="space-y-6">
        {summary && (
          <Card>
            <CardContent className="flex flex-wrap items-center gap-6 pt-6">
              <div>
                <div className="text-xs text-muted-foreground">Outstanding</div>
                <div className="text-xl font-semibold tabular-nums">
                  {formatPaise(summary.outstanding_paise)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Active Loans</div>
                <div className="text-xl font-semibold tabular-nums">
                  {summary.disbursed_count}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loansQ.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t load your loans</AlertTitle>
            <AlertDescription>{toApiError(loansQ.error).message}</AlertDescription>
          </Alert>
        ) : (
          <LoanTable
            loans={loans}
            isLoading={loansQ.isLoading || selfId == null}
            tenantSlug={tenantSlug}
            showEmployee={false}
          />
        )}
      </div>
    </MeModuleShell>
  );
}
